import { useToastStore } from '@/store';
import type { Store } from 'pinia';

type ToastType = 'success' | 'fail' | 'message' | 'loading';

type TaskType = {
  message?: string;
  type?: ToastType;
  key?: string;
  duration?: number;
  callback?: () => void;
};

const MAX_TIME = 6000;
const COMMON_TIME = 1000;
const DEFAULT_LOADING_TEXT = '加载中';

class Toast {
  private queue: TaskType[] = [];
  private isProcessing = false;
  private timer: string | number | NodeJS.Timeout | undefined;
  private toastStore!: Store<
    'toast',
    {
      isVisible: boolean;
      message: string;
      type: string;
    },
    {},
    {}
  >;

  /**
   * 推入任务
   */
  private enqueue(task: any): void {
    this.queue.push(task);
    this.executeQueue();
  }

  /**
   * 处理任务队列
   * @returns
   */
  private async executeQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    const currentTask = this.queue.shift()!;

    this.showToast(currentTask);

    this.timer = setTimeout(async () => {
      this.timer = undefined;
      await this.hideToast();
      currentTask.callback?.();
      this.isProcessing = false;
      this.executeQueue();
    }, currentTask.duration);
  }

  /**
   * 开启toast
   * @param options
   */
  private async showToast(options: AnyObject) {
    const store = this.getStore();
    store.isVisible = true;
    store.message = options.message;
    store.type = options.type;
  }

  /**
   * 隐藏toast
   */
  private async hideToast() {
    const store = this.getStore();
    store.isVisible = false;
  }

  /**
   * 隐藏loading
   */
  private async hideLoading() {
    // 需判断当前是否loading中
    // console.log("hideLoading called");
    this.timer && clearTimeout(this.timer);
    await this.hideToast();
    this.isProcessing = false;
    this.executeQueue();
  }

  /**
   * 获取store状态仓库
   * @returns
   */
  private getStore() {
    if (!this.toastStore) {
      this.toastStore = useToastStore();
    }
    return this.toastStore;
  }

  /**
   * 创建普通任务
   */
  private createCommonTask(type: ToastType, options: TaskType) {
    return {
      type,
      message: options.message,
      callback: options.callback,
      duration: options.duration || COMMON_TIME,
      key: new Date().getTime().toString(),
    };
  }

  /**
   * 创建加载任务
   */
  private createLoadingTask(type: ToastType, options: TaskType) {
    return {
      target: {
        type,
        message: options.message || DEFAULT_LOADING_TEXT,
        duration: options.duration || MAX_TIME,
        key: new Date().getTime().toString(),
      },
      cancelFn: this.hideLoading.bind(this),
    };
  }

  /**
   * 清空队列任务
   */
  public clear() {
    // Clear the timer first to prevent any pending callbacks
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }

    // Reset all state
    this.queue = [];
    this.isProcessing = false;

    // Update store directly instead of calling hideToast
    const store = this.getStore();
    store.isVisible = false;
  }

  public message(message: string, callback?: () => void, duration?: number) {
    const task = this.createCommonTask('message', { message, callback, duration });
    this.enqueue(task);
  }

  public loading(message?: string, duration?: number) {
    const task = this.createLoadingTask('loading', { message, duration });
    this.enqueue(task.target);
    return task.cancelFn;
  }
}

export const toast = new Toast();
